import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { priceBookApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, BookOpen } from 'lucide-react';
import { fmtMoney } from '@/lib/format';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { Textarea } from '@/components/ui/textarea';

export default function PriceBookPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: 'service', name: '', description: '', unit_price: 0, unit_cost: 0, taxable: true, category: '' });

  const load = async () => {
    try {
      setItems(await priceBookApi.list());
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await priceBookApi.create({ ...form, unit_price: parseFloat(form.unit_price) || 0, unit_cost: parseFloat(form.unit_cost) || 0 });
      toast.success('Added'); setOpen(false); setForm({ kind: 'service', name: '', description: '', unit_price: 0, unit_cost: 0, taxable: true, category: '' }); load();
    } catch { toast.error('Failed'); }
  };

  const del = async (id) => { await priceBookApi.delete(id); load(); };

  const services = items.filter(i => i.kind === 'service');
  const materials = items.filter(i => i.kind === 'material');

  return (
    <AppShell title="Price Book">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Price Book</h1>
          <p className="text-sm text-muted-foreground">Reusable services and materials for estimates and jobs.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New item</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New price book item</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Type</Label>
                <select className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>
                  <option value="service">Service</option><option value="material">Material</option>
                </select>
              </div>
              <div><Label>Name *</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Price</Label><Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} /></div>
                <div><Label>Cost</Label><Input type="number" step="0.01" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: e.target.value })} /></div>
              </div>
              <div><Label>Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
              <DialogFooter><Button type="submit">Add</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="services">
        <TabsList>
          <TabsTrigger value="services">Services ({services.length})</TabsTrigger>
          <TabsTrigger value="materials">Materials ({materials.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-4">
          {!loaded ? <ListSkeleton /> : services.length === 0 ? <EmptyState icon={BookOpen} title="No services yet" description="Add services to reuse in jobs and estimates." /> : (
            <Card><CardContent className="p-0"><div className="divide-y">
              {services.map(s => (
                <div key={s.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                  <div><div className="font-medium text-sm">{s.name}</div>{s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}</div>
                  <div className="flex items-center gap-3">
                    <div className="font-semibold tabular-nums">{fmtMoney(s.unit_price)}</div>
                    <Button size="icon" variant="ghost" onClick={() => del(s.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div></CardContent></Card>
          )}
        </TabsContent>
        <TabsContent value="materials" className="mt-4">
          {!loaded ? <ListSkeleton /> : materials.length === 0 ? <EmptyState icon={BookOpen} title="No materials yet" /> : (
            <Card><CardContent className="p-0"><div className="divide-y">
              {materials.map(s => (
                <div key={s.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                  <div><div className="font-medium text-sm">{s.name}</div>{s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}</div>
                  <div className="flex items-center gap-3">
                    <div className="font-semibold tabular-nums">{fmtMoney(s.unit_price)}</div>
                    <Button size="icon" variant="ghost" onClick={() => del(s.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
